import React, { useState, useEffect } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../BaseUrl/ApiClient';
import { useParams, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Input, Form, FormGroup, Label } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './UserDetail.css';

function UpdateSubscriptionType() {
  const history = useHistory();
  const { businessId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [businessData, setBusinessData] = useState(null);

  const [subscriptionType, setSubscriptionType] = useState('standard');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [subscriptionExpiryDate, setSubscriptionExpiryDate] = useState('');
  const [subscriptionMethod, setSubscriptionMethod] = useState('');

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        const response = await ApiClient.get(
          `/business/admin/${businessId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.data) {
          setBusinessData(response.data.responseData.business);

          if (response.data.responseData.business?.subscriptionType) {
            setSubscriptionType(response.data.responseData.business?.subscriptionType);
          }

          const startDate = response.data.responseData.business?.subscription?.subscriptionStartDate
            ? new Date(response.data.responseData.business.subscription.subscriptionStartDate).toISOString().split('T')[0]
            : '';

          const expiryDate = response.data.responseData.business?.subscription?.subscriptionExpiryDate
            ? new Date(response.data.responseData.business.subscription.subscriptionExpiryDate).toISOString().split('T')[0]
            : '';

          setSubscriptionStartDate(startDate);
          setSubscriptionExpiryDate(expiryDate);
          setSubscriptionStatus(response.data.responseData.business?.subscription?.subscriptionStatus || '');
          setSubscriptionMethod(response.data.responseData.business?.subscription?.subscriptionMethod || '');

        } else {
          toast.error('Business data not found');
        }
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("authUser");
          window.location.href = "/login";
        }
      } finally {
        setIsFetching(false);
      }
    };

    fetchBusinessData();
  }, [businessId]);



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subscriptionType) {
      toast.error('Subscription type is required');
      return;
    }

    // Check if changing from Subscribed to Expired for cash/e-transfer
    const isCashOrETransfer = ['cash', 'e-transfer'].includes(subscriptionMethod);
    const isChangingToExpired = subscriptionStatus === 'Expired' &&
      businessData?.subscription?.subscriptionStatus === 'Subscribed';

    let finalExpiryDate = subscriptionExpiryDate;

    // If changing from Subscribed to Expired for cash/e-transfer, set expiry to today
    if (isCashOrETransfer && isChangingToExpired) {
      finalExpiryDate = new Date().toISOString().split('T')[0];
      setSubscriptionExpiryDate(finalExpiryDate);
    }

    setIsLoading(true);
    try {
      const userId = businessData.userId || businessData.user?._id || businessData.user?.id;

      if (!userId) {
        toast.error('User ID not found');
        return;
      }

      const payload = {
        subscriptionType,
        userId,
        ...(subscriptionStatus && { subscriptionStatus }),
        ...(subscriptionStartDate && { subscriptionStartDate }),
        ...(finalExpiryDate && { subscriptionExpiryDate: finalExpiryDate }),
        ...(subscriptionMethod && { subscriptionMethod })
      };

      const response = await ApiClient.put(
        `/admin/subscription/user/${userId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data) {
        toast.success('Subscription updated successfully!');
        setTimeout(() => {
          history.push('/business-list');
        }, 2000);
      } else {
        toast.error('Failed to update subscription');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response) {
        toast.error(error.response.data?.message || 'Update failed');
        if (error.response?.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("authUser");
          window.location.href = "/login";
        }
      } else {
        toast.error('Network error. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check if subscription is Stripe and active
  const isStripeActiveSubscription = subscriptionMethod === 'stripe' && subscriptionStatus === 'Subscribed';
  // Check if subscription is Stripe and expired
  const isStripeExpiredSubscription = subscriptionMethod === 'stripe' && subscriptionStatus === 'Expired';
  // Check if subscription is cash/e-transfer
  const isCashOrETransfer = ['cash', 'e-transfer'].includes(subscriptionMethod);
  // Check if subscription is cash/e-transfer and expired
  const isCashOrETransferExpired = isCashOrETransfer && subscriptionStatus === 'Expired';

  // Function to handle expiry date change for Stripe subscriptions
  const handleExpiryDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0];

    if (isStripeActiveSubscription && selectedDate < today) {
      toast.error('Cannot select past date for active Stripe subscription');
      return;
    }

    setSubscriptionExpiryDate(selectedDate);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setSubscriptionStatus(newStatus);

    // If changing to Expired for cash/e-transfer (from Subscribed), set expiry to today
    if (newStatus === 'Expired' && isCashOrETransfer &&
      businessData?.subscription?.subscriptionStatus === 'Subscribed') {
      setSubscriptionExpiryDate(new Date().toISOString().split('T')[0]);
    }
  };



  if (isFetching) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12} className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading business data...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12} className="text-center my-5">
              <p>Business not found</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <MetaTags>
          <title>Update Subscription| Findagram</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h3>Update Subscription</h3>
                  {businessData.businessName && (
                    <p className="mb-1 mt-3">
                      <strong>Business Name:</strong> {businessData.businessName}
                    </p>
                  )}
                  <div className="business-info">
                    {businessData.user && (
                      <p className="mb-0">
                        <strong>User Email:</strong> {businessData.user.email || 'N/A'}
                      </p>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    <FormGroup>
                      <Row>
                        <Col md="6">
                          <Label for="subscriptionStatus">Subscription Status</Label>
                          <Input
                            type="select"
                            id="subscriptionStatus"
                            value={subscriptionStatus}
                            disabled={isStripeActiveSubscription}
                            onChange={handleStatusChange}
                          >
                            <option value="">Select Status</option>
                            <option value="Expired">Expired</option>
                            <option value="Subscribed">Subscribed</option>
                          </Input>
                        </Col>

                        <Col md="6">
                          <Label for="subscriptionMethod">Subscription Method</Label>
                          <Input
                            type="select"
                            id="subscriptionMethod"
                            value={subscriptionMethod}
                            disabled={isStripeActiveSubscription}
                            onChange={(e) => setSubscriptionMethod(e.target.value)}
                          >
                            <option value="">Select Method</option>
                            {/* <option value="cash">Cash</option> */}
                                    {!isCashOrETransfer && <option value="stripe">Credit Card</option>}

                            <option value="e-transfer">E-Transfer</option>
                          </Input>
                        </Col>
                      </Row>
                    </FormGroup>

                    <FormGroup>
                      <Row>
                        <Col md="6">
                          <Label for="subscriptionStartDate">Subscription Start Date</Label>
                          <Input
                            type="date"
                            id="subscriptionStartDate"
                            value={subscriptionStartDate}
                            onChange={(e) => setSubscriptionStartDate(e.target.value)}
                            disabled={isStripeActiveSubscription}
                          />
                        </Col>
                        <Col md="6">
                          <Label for="subscriptionExpiryDate">Subscription Expiry Date</Label>
                          <Input
                            type="date"
                            id="subscriptionExpiryDate"
                            value={subscriptionExpiryDate}
                            onChange={isStripeActiveSubscription ? handleExpiryDateChange : (e) => setSubscriptionExpiryDate(e.target.value)}
                            min={isStripeActiveSubscription ? new Date().toISOString().split('T')[0] : undefined}
                          />
                        </Col>
                      </Row>
                    </FormGroup>

                    <FormGroup>
                      <Row>
                        <Col>
                          <Label>Subscription Type (Required)</Label>
                          <div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="subscriptionType"
                                id="standard"
                                value="standard"
                                checked={subscriptionType === "standard"}
                                onChange={(e) => setSubscriptionType(e.target.value)}
                                disabled={isLoading}
                                required
                              />
                              <label className="form-check-label" htmlFor="standard">
                                Standard
                              </label>
                            </div>

                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="subscriptionType"
                                id="featured"
                                value="featured"
                                checked={subscriptionType === "featured"}
                                onChange={(e) => setSubscriptionType(e.target.value)}
                                disabled={isLoading}
                              />
                              <label className="form-check-label" htmlFor="featured">
                                Featured
                              </label>
                            </div>

                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="subscriptionType"
                                id="sponsored"
                                value="sponsored"
                                checked={subscriptionType === "sponsored"}
                                onChange={(e) => setSubscriptionType(e.target.value)}
                                disabled={isLoading}
                              />
                              <label className="form-check-label" htmlFor="sponsored">
                                Sponsored
                              </label>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </FormGroup>

                    <div className='d-flex justify-content-end'>
                      <Col lg="3" xs="12" md="5" className="mt-3 mt-md-0 text-end">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isLoading}
                          className="w-100"
                        >
                          {isLoading ? 'Updating...' : 'Update Subscription'}
                        </Button>
                      </Col>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default UpdateSubscriptionType;